import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, object } from 'prop-types';

import { useDispatch } from 'react-redux';

import { useFormik } from 'formik';

import useQueue from '../../../hooks/useQueue';

import ConfirmActionModal from '../ConfirmActionModal';
import TextField from '../../../components/FormFields/TextField';

import { enqueueSnackbar } from '../../../actions/applicationMessageActions';
import { recalculateRule } from '../../../actions/ruleActions';

import validationSchema from './Recalculation.schema';

import classes from './RuleRecalculateModal.module.css';

const RuleRecalculateModal = ({
    onClose,
    open,
    rules,
    ...props
}) => {
    const [ isLoading, setIsLoading ] = useState(false);

    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: {
            end:   '',
            start: ''
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            runQueue(rules);
        }
    });

    const [queue, runQueue] = useQueue(async (rule, { fulfill, reject }) => {
        try {
            await dispatch(recalculateRule({
                end:   formik.values.end,
                id:    rule.id,
                start: formik.values.start
            }));
            fulfill({
                item:  rule,
                value: true
            });
        } catch (error) {
            reject({
                error,
                item: rule
            });
        }
    });

    useEffect(() => {
        if (queue.complete && queue.successful) {
            dispatch(
                enqueueSnackbar('Rule(s) recalculation started.', 'success')
            );
            setIsLoading(false);
            onClose();
        } else if (queue.pending) {
            setIsLoading(true);
        }
    }, [queue.complete, queue.successful, queue.pending]);

    return (
        <ConfirmActionModal
            confirmationPhrase="recalculate"
            disabled={!formik.isValid}
            onConfirm={formik.handleSubmit}
            onClose={onClose}
            open={open}
            isLoading={isLoading}
            title="Recalculate Rule(s)?"
            {...props}
        >
            <p>
                Recalculating a Rule will remove all historical Rule logs for the selected Rules and reevaluate the the Rule Formula to determine updated Faults and Scoring.
            </p>
            <p>
                Rule recalculation can take a significant amount of time based on the amount of Rule Targets and the amount of data being evaluated for each Target. Unless otherwise specified, the Rule recalculation will start with the most recent data and work backwards through time until the recalculation is complete.
            </p>
            <div className={classes.fieldset}>
                <TextField
                    errors={formik.errors.start}
                    label="Start"
                    name="start"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    touched={formik.errors.start}
                    type="date"
                    value={formik.values.start}
                />
                <TextField
                    errors={formik.errors.end}
                    label="End"
                    name="end"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    touched={formik.touched.end}
                    type="date"
                    value={formik.values.end}
                />
            </div>
            <p>
                Are you sure you want to recalculate these rule(s)?
            </p>
            <ul>
                {rules.map(({ id, name }) => (
                    <li key={id}>{name}</li>
                ))}
            </ul>
        </ConfirmActionModal>
    );
};

RuleRecalculateModal.defaultProps = {
    onClose: () => {},
    open:    false,
    rules:   []
};

RuleRecalculateModal.propTypes = {
    onClose: func,
    open:    bool,
    rules:   arrayOf(object)
};

export default RuleRecalculateModal;
